import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import toast from "react-hot-toast";

const UploadRuleExcelDialog = ({
  open,
  onClose = () => {},
  productId,
  onUploadSuccess = () => {},
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");

  const resetState = () => {
    setSelectedFile(null);
    setIsUploading(false);
    setError("");
  };

  const handleClose = () => {
    if (isUploading) return;
    resetState();
    onClose();
  };

  const handleFileChange = (event) => {
    setError("");
    const file = event.target.files?.[0] || null;
    if (!file) {
      setSelectedFile(null);
      return;
    }

    const isValidExtension = /\.(xlsx|xls)$/i.test(file.name);
    if (!isValidExtension) {
      setError("Please upload a valid Excel file with .xlsx or .xls format.");
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedFile) {
      setError("Please select an Excel file before uploading.");
      return;
    }
    if (!productId) {
      setError("Missing product id. Please try again from the product context.");
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("product_id", productId);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/bulk-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
          validateStatus: (status) =>
            [200, 205, 207].includes(status) || (status >= 200 && status < 300),
        }
      );

      if (response.status === 200) {
        toast.success("Excel uploaded successfully. Rules are being processed.");
        onUploadSuccess();
        handleClose();
        return;
      }

      if (response.status === 205 || response.status === 207) {
        const blob = new Blob([response.data], {
          type:
            response.headers["content-type"] ||
            "text/csv",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "rule-upload-errors.csv";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.error(
          "Upload completed with errors. Please review the downloaded file."
        );
        return;
      }

      toast.success("Excel uploaded successfully. Rules are being processed.");
      onUploadSuccess();
      handleClose();
    } catch (uploadError) {
      console.error("Error uploading excel file:", uploadError);
      const message =
        uploadError?.response?.data?.message ||
        uploadError?.message ||
        "Failed to upload excel file. Please try again.";
      setError(message);
      toast.error("Failed to upload excel file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pr: 2,
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Rules via Excel</DialogTitle>
        <IconButton onClick={handleClose} disabled={isUploading}>
          <CloseIcon />
        </IconButton>
      </Box>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Alert
              icon={<InfoOutlinedIcon fontSize="inherit" />}
              severity="info"
              sx={{ borderRadius: 2 }}
            >
              Upload an Excel file containing rule configurations. Ensure the file
              has all required columns and valid JSON strings where applicable.
            </Alert>

            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Expected Columns
              </Typography>
              <Typography variant="body2" color="text.secondary">
                data_asset_name, dimension_check, check_id, arguments, filter,
                list_of_columns, rule_name, rule_description, threshold,
                threshold_type, created_by, keywords, sql_query, rule_owner,
                rule_phase
              </Typography>
            </Box>

            <Box
              sx={{
                border: "1px dashed #bb1ccc",
                borderRadius: 2,
                padding: 3,
                textAlign: "center",
                backgroundColor: "#f8f9ff",
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: "#bb1ccc", mb: 1 }} />
              <Typography variant="h6" fontWeight={600}>
                Drag & Drop or Select File
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Supports .xlsx and .xls files up to 5 MB
              </Typography>
              <Button
                variant="outlined"
                component="label"
                sx={{
                  color: "#bb1ccc",
                  borderColor: "#bb1ccc",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#bb1ccc",
                    backgroundColor: "rgba(0, 0, 160, 0.08)",
                  },
                  "&:disabled": {
                    borderColor: "#9ca3af",
                    color: "#9ca3af",
                  },
                }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
              </Button>
              {selectedFile && (
                <Typography
                  variant="body2"
                  sx={{ mt: 2, fontWeight: 600, color: "#bb1ccc" }}
                >
                  Selected: {selectedFile.name}
                </Typography>
              )}
            </Box>

            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button sx={{
            color: "#bb1ccc"
          }} onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isUploading || !selectedFile}
            sx={{
              backgroundColor: "#bb1ccc",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#bb1ccc",
              },
              "&:disabled": {
                backgroundColor: "#9ca3af",
              },
            }}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UploadRuleExcelDialog;

